import omit from 'lodash/omit';

import {
  Model,
  ModelData,
  ModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';

export const addProjectModelIdsToIri =
  (iri: string, projectModelIds: string[]) => (model: Model) => {
    const existingProjectModelIds = new Set(
      model.modelData.modelsByIri[iri] || []
    );

    projectModelIds.forEach((projectModelId) => {
      existingProjectModelIds.add(projectModelId);
    });

    setDataProperty<ModelData>(model)(model.modelData, 'modelsByIri', {
      ...model.modelData.modelsByIri,
      [iri]: Array.from(existingProjectModelIds),
    });
  };

/** remove this iri entirely */
export const removeProjectModelIri = (iri: string) => (model: Model) => {
  setDataProperty<ModelData>(model)(
    model.modelData,
    'modelsByIri',
    omit(model.modelData.modelsByIri, iri)
  );
};

/** remove this model id from every iri
 *
 * Must make sure it is safe to have empty projectModelIds, since we
 * could be removing the last model id in the list
 */
export const removeProjectModelId =
  (projectModelId: string) => (model: Model) => {
    setDataProperty<ModelData>(model)(
      model.modelData,
      'modelsByIri',
      Object.fromEntries(
        Object.entries(model.modelData.modelsByIri).map(
          ([iri, projectModelIds]) => [
            iri,
            projectModelIds.filter((id) => id !== projectModelId),
          ]
        )
      )
    );
  };

/** remove these model ids for this iri */
export const removeProjectModelIdsForIri =
  (iri: string, projectModelIds: string[]) => (model: Model) => {
    const existingProjectModelIds = new Set(model.modelData.modelsByIri[iri]);

    projectModelIds.forEach((projectModelId) => {
      existingProjectModelIds.delete(projectModelId);
    });

    if (!existingProjectModelIds.size) {
      // do not allow empty model ids
      return;
    }

    setDataProperty<ModelData>(model)(model.modelData, 'modelsByIri', {
      ...model.modelData.modelsByIri,
      [iri]: Array.from(existingProjectModelIds),
    });
  };

export const getProjectModelIdsForIri = (
  iri: string,
  model: Model | ModelSnapshot
) => model.modelData.modelsByIri[iri] || [];

export const getProjectIrisForModelId = (
  modelId: string,
  model: Model | ModelSnapshot
) =>
  Object.keys(model.modelData.modelsByIri).filter((iri) => {
    const isInModel = model.modelData.modelsByIri[iri].includes(modelId);
    if (!isInModel) {
      return false;
    }
    // don't include attributes
    return !model.modelData.attributesByIri[iri];
  });
